/** Homepage presentation and controls; scrolling remains owned by Tinker. */
class CdailyHomeProductRail extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
    this.rail = this.querySelector('.resource-list__carousel > slideshow-container > slideshow-slides');
    if (!this.rail) return;
    this.controller = new AbortController();
    this.arrows = this.rail.parentElement.querySelector(':scope > slideshow-arrows');
    this.previous = this.arrows?.querySelector('[ref="previous"]');
    this.next = this.arrows?.querySelector('[ref="next"]');
    for (const [button, label] of [[this.previous, 'Previous products'], [this.next, 'Next products']]) {
      if (!button) continue;
      button.type = 'button';
      button.setAttribute('aria-label', label);
      // A small currentColor chevron, scoped to the outer rail (never card galleries).
      button.innerHTML = '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="m7 4 5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }
    this.mobile = matchMedia('(max-width: 749px)');
    this.mobile.addEventListener('change', () => this.updateFeaturedProduct(), { signal: this.controller.signal });
    this.schedule = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    };
    this.rail.addEventListener('scroll', this.schedule, { passive: true, signal: this.controller.signal });
    this.rail.addEventListener('scrollend', this.schedule, { signal: this.controller.signal });
    this.resize = new ResizeObserver(this.schedule);
    this.resize.observe(this.rail);
    const gallery = this.rail.querySelector('.card-gallery');
    if (gallery) this.resize.observe(gallery);
    this.updateFeaturedProduct();
    this.schedule();
  }

  updateFeaturedProduct() {
    const featuredId = this.dataset.mobileFeaturedId;
    if (!featuredId) return;
    for (const slide of this.rail.children) {
      if (slide.querySelector('product-card')?.dataset.productId !== featuredId) continue;
      // Tinker's public slides getter already excludes [hidden] slides.
      slide.toggleAttribute('hidden', this.mobile.matches);
      slide.removeAttribute('reveal');
    }
    this.toggleAttribute('data-mobile-empty', [...this.rail.children].every(slide => slide.hidden));
    const slider = this.querySelector('slideshow-component');
    customElements.whenDefined('slideshow-component').then(() => {
      if (this.isConnected) slider?.select(0, undefined, { animate: false });
    });
  }

  update() {
    const bounds = this.rail.getBoundingClientRect();
    const slides = [...this.rail.children].filter(slide => !slide.hidden);
    const gallery = slides[0]?.querySelector('.card-gallery');
    // Finish geometry reads before changing styles or attributes.
    const slideBounds = [...this.rail.children].map(slide => [slide, slide.getBoundingClientRect()]);
    const rtl = getComputedStyle(this.rail).direction === 'rtl';
    const offset = Math.abs(this.rail.scrollLeft);
    const end = this.rail.scrollWidth - this.rail.clientWidth;
    if (gallery) {
      const image = gallery.getBoundingClientRect();
      const container = this.rail.parentElement.getBoundingClientRect();
      const position = `${image.top - container.top + image.height / 2}px`;
      if (this.style.getPropertyValue('--cdh-controls-y') !== position) this.style.setProperty('--cdh-controls-y', position);
    }
    // Use actual scroll boundaries, including a last page with fewer visible cards.
    if (this.previous) this.previous.disabled = offset <= 2;
    if (this.next) this.next.disabled = offset >= end - 2;
    this.arrows?.toggleAttribute('data-rtl', rtl);
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    for (const [slide, rect] of slideBounds) {
      const visible = rect.right > bounds.left && rect.left < bounds.right;
      const partial = rect.left < bounds.left - 1 || rect.right > bounds.right + 1;
      slide.toggleAttribute('data-cdaily-peek', mobile && visible && partial);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resize?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

if (!customElements.get('cdaily-home-product-rail')) {
  customElements.define('cdaily-home-product-rail', CdailyHomeProductRail);
}
